import type { ReactNode } from 'react';
import type { ThreadItem } from './chat-types';
import markUrl from '@/public/logo/mark-color.svg';
import { ChatBubble, TypingBubble } from './ChatBubble';

type ChatThreadProps = {
  thread: readonly ThreadItem[];
  typing: boolean;
  /** Rendered inside the scroll area after the last message, e.g. the signup form card. */
  children?: ReactNode;
};

const isVisitor = (item: ThreadItem | undefined): boolean => item?.kind === 'text' && item.from === 'me';

/**
 * A group is a run of Pertento's messages. It opens after the visitor speaks, at
 * the start, and after a date label, since a new announcement is a new thing being
 * said rather than a continuation of the last one.
 */
const startsGroup = (previous: ThreadItem | undefined): boolean =>
  !previous || isVisitor(previous) || previous.kind === 'date';

/** 4px inside a run of Pertento bubbles, 18px before a new group or a visitor message. */
const gapBefore = (previous: ThreadItem | undefined, item: ThreadItem | null): string => {
  if (!previous) return '';
  if (!item || isVisitor(item) || item.kind === 'date' || startsGroup(previous)) return 'mt-[18px]';
  return 'mt-1';
};

const GroupHeader = () => (
  <div className="pt-msg-in mb-2.5 flex items-center gap-2">
    <span className="border-line bg-panel flex h-7 w-7 items-center justify-center rounded-lg border p-[5px]">
      <img src={markUrl} alt="" className="h-[15px] w-auto" />
    </span>
    <span className="text-dim text-micro">The Pertento team</span>
  </div>
);

/**
 * column-reverse keeps the newest message pinned to the bottom as the thread grows
 * without any scroll bookkeeping: the scroll origin is the bottom edge. The inner
 * column carries auto margins, so a short thread sits vertically centred rather
 * than glued to the bottom, and a long one fills the area and scrolls.
 *
 * The mask fades the oldest messages out at the top instead of cutting them.
 */
export const ChatThread = ({ thread, typing, children }: ChatThreadProps) => {
  const last = thread.at(-1);

  return (
    <div className="flex min-h-0 flex-1 flex-col-reverse overflow-y-auto [mask-image:linear-gradient(to_bottom,transparent_0,#000_64px)]">
      {/* The top padding is room for the fade mask; on a phone it is just dead space
          above the first message. */}
      <div className="my-auto flex w-full flex-col px-0.5 pt-4 pb-1 min-[910px]:pt-12">
        {/* The live region is the messages only. The signup form and the question
            cards are children of this column too, and inside the region their
            validation errors were announced unpredictably and every re-render of a
            field risked a re-read. They sit after it, outside. */}
        <div role="log" aria-live="polite" aria-label="Conversation with Pertento" className="flex flex-col">
          {thread.map((item, idx) => {
            const previous = thread[idx - 1];
            const opensGroup = item.kind !== 'date' && !isVisitor(item) && startsGroup(previous);

            return (
              <div key={item.id} className={gapBefore(previous, item)}>
                {opensGroup && <GroupHeader />}
                <ChatBubble message={item} />
              </div>
            );
          })}

          {typing && (
            <div className={gapBefore(last, null)}>
              {startsGroup(last) && <GroupHeader />}
              <TypingBubble />
            </div>
          )}
        </div>

        {children}
      </div>
    </div>
  );
};
